import React, { useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";

/**
 * Phase 11 D — Reusable confirmation modal for soft-deleting any resource
 * (lead, booking, Jarvis conversation, recruiting application, call log).
 *
 * Props
 *  - open              boolean
 *  - onClose           () => void
 *  - onConfirm         async (reason: string) => void
 *  - resourceLabel     "lead" | "booking" | "conversation" | "recruiting application" | "call log"
 *  - resourceName      Optional string shown in bold ("Priya Ramanathan", "#POL-201")
 *  - testidPrefix      Optional data-testid prefix (default "confirm-delete")
 */
export default function ConfirmDeleteModal({
    open,
    onClose,
    onConfirm,
    resourceLabel = "record",
    resourceName = "",
    testidPrefix = "confirm-delete",
}) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!open) return null;

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await onConfirm(reason.trim());
            setReason("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            role="dialog"
            aria-modal="true"
            data-testid={`${testidPrefix}-backdrop`}
            onClick={(e) => {
                if (e.target === e.currentTarget && !submitting) onClose?.();
            }}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                data-testid={`${testidPrefix}-modal`}
            >
                <div className="flex items-start gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-700">
                        <AlertTriangle size={20} />
                    </span>
                    <div className="flex-1">
                        <h3 className="mm-display text-lg text-mm-navy">
                            Delete this {resourceLabel}?
                        </h3>
                        {resourceName && (
                            <p className="mt-1 text-sm text-mm-ink">
                                <strong>{resourceName}</strong>
                            </p>
                        )}
                        <p className="mt-2 text-sm text-mm-muted">
                            It will be moved to archive and permanently deleted in 30 days.
                            You can restore it from the archive filter until then.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => !submitting && onClose?.()}
                        className="text-mm-muted hover:text-mm-ink"
                        aria-label="Close"
                        disabled={submitting}
                        data-testid={`${testidPrefix}-close`}
                    >
                        <X size={16} />
                    </button>
                </div>

                <label className="mt-5 block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-mm-muted">
                        Reason (optional)
                    </span>
                    <input
                        className="mm-input"
                        placeholder="e.g. duplicate, test data, wrong lead"
                        value={reason}
                        maxLength={280}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={submitting}
                        data-testid={`${testidPrefix}-reason`}
                    />
                </label>

                <div className="mt-6 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => !submitting && onClose?.()}
                        disabled={submitting}
                        className="rounded-full border border-mm-line bg-white px-4 py-2 text-sm font-medium text-mm-navy hover:bg-mm-bg disabled:opacity-50"
                        data-testid={`${testidPrefix}-cancel`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-red-700 disabled:opacity-60"
                        data-testid={`${testidPrefix}-submit`}
                    >
                        {submitting && <Loader2 size={14} className="animate-spin" />}
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}
